import clsx from 'clsx'
import {ACCESS_TOKEN} from '@constants/auth.constant'
import {LocalStorageService} from '@services/utils/localStorage.service'
import {axiosIns} from 'config/api.config'
import {ENV} from 'config/ENV.config'
import {FC, useCallback, useEffect, useState} from 'react'

/** Chat's app key in portal's module catalog. */
const CHAT_APP_KEY = 'ERPBOI_CHAT'
/** How often the badge re-checks (ms); it also re-checks whenever this window regains focus. */
const POLL_MS = 60000

type Unread = {messages: number; chats: number; mentions: number}

const toUrl = (host?: string) => (!host ? '' : /^https?:\/\//i.test(host) ? host : `https://${host}`)

/**
 * The Chat launcher in the topbar: an icon with this person's unread count (and an "@" when someone
 * mentioned them), opening the Chat console in its own tab, as the module switcher does.
 *
 * Shown only when the company has Chat (portal's purchased modules, which also give its host) and
 * chat-service answers GET /unread/summary for this person, so someone without chat access never
 * sees it. One cheap request a minute and on focus; no socket.
 *
 * The summary is fetched with plain fetch, NOT axiosIns: that client logs the user out on a
 * `code: 401` answer, and a misconfigured chat-service must never sign anybody out of this console.
 * Any failure simply hides the launcher.
 */
export const ChatLauncher: FC<{itemClass: string; btnClass: string}> = ({itemClass, btnClass}) => {
  const [url, setUrl] = useState('')
  const [unread, setUnread] = useState<Unread | null>(null)

  useEffect(() => {
    if (!ENV.chatGetway) return
    axiosIns
      .get('/module/get-purchased-modules', {baseURL: ENV.portalGetway})
      .then((res: any) => {
        const chat = (res?.body || []).find((m: any) => m?.appKey === CHAT_APP_KEY && m?.hostOrigin)
        setUrl(chat ? toUrl(chat.hostOrigin) : '')
      })
      .catch(() => setUrl(''))
  }, [])

  const load = useCallback(() => {
    const token = LocalStorageService.get(ACCESS_TOKEN)
    if (!token) return
    fetch(`${ENV.chatGetway}/unread/summary`, {
      headers: {Authorization: `Bearer ${token}`, Accept: 'application/json'},
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) =>
        setUnread(
          data?.code === 200 && data.body
            ? {
                messages: Number(data.body.messages) || 0,
                chats: Number(data.body.chats) || 0,
                mentions: Number(data.body.mentions) || 0,
              }
            : null
        )
      )
      .catch(() => setUnread(null))
  }, [])

  useEffect(() => {
    if (!url) return
    load()
    const timer = setInterval(load, POLL_MS)
    window.addEventListener('focus', load)
    return () => {
      clearInterval(timer)
      window.removeEventListener('focus', load)
    }
  }, [url, load])

  if (!url || !unread) return null
  const count = unread.messages > 0 ? unread.messages : unread.chats
  const title =
    unread.mentions > 0
      ? `Chat: you were mentioned (${unread.mentions})`
      : count > 0
      ? `Chat: ${count} unread`
      : 'Chat'

  return (
    <div className={clsx('d-flex align-items-center', itemClass)}>
      <a
        href={`${url}/chats`}
        target='_blank'
        rel='noreferrer'
        title={title}
        aria-label={title}
        className={clsx(btnClass, 'position-relative')}
      >
        <i className='bi bi-chat-dots fs-2' />
        {count > 0 && (
          <span className='badge badge-circle badge-danger position-absolute top-0 start-100 translate-middle fs-9'>
            {count > 99 ? '99+' : count}
          </span>
        )}
        {unread.mentions > 0 && (
          <span className='badge badge-circle badge-primary position-absolute top-100 start-100 translate-middle fs-9'>
            @
          </span>
        )}
      </a>
    </div>
  )
}
